import { sendAnalytics } from '@audio-network/analytics'
import { Chip, Fieldset, TextInput } from '@audio-network/componentlibrary'
import { Button } from '@audio-network/componentlibrary'
import debounce from 'lodash/debounce'
import { useTranslation } from 'next-i18next'
import { FormEvent } from 'react'

import BeatCalculatorIcon from './BeatCalculatorIcon'
import { useBpmRange, useTempo } from './TempoBpm.hooks'
import styles from './TempoBpm.module.css'

const TempoBpm = (
	props: React.DetailedHTMLProps<
		React.HTMLAttributes<HTMLDivElement>,
		HTMLDivElement
	>
): JSX.Element => {
	const { t } = useTranslation(['search'])

	const { tempos, handleTempoChange } = useTempo()

	const {
		handleBpmInput,
		openingBpmMin,
		openingBpmMax,
		openingBpmMinRef,
		openingBpmMaxRef,
		OPENING_BPM_MIN,
		OPENING_BPM_MAX,
		closingBpmMin,
		closingBpmMax,
		closingBpmMinRef,
		closingBpmMaxRef,
		CLOSING_BPM_MIN,
		CLOSING_BPM_MAX,
		defaultOpeningBpmMin,
		defaultOpeningBpmMax,
		defaultClosingBpmMin,
		defaultClosingBpmMax,
	} = useBpmRange()

	const showBeatCalculator = (event: FormEvent): void => {
		event.preventDefault()
		event.stopPropagation()
		const wHeight = navigator.userAgent.match(/Version\/[\d.]+.*Safari/)
			? 475
			: 430
		window.open(
			'/beatcalculator',
			'',
			'width=320,height=' + wHeight + ',left=360,top=190'
		)
		sendAnalytics({
			noun: 'Beat Calculator',
			pastTenseVerb: 'Clicked',
		})
	}

	return (
		<div className={styles.tempoBpm} {...props}>
			<Fieldset direction="row" legend={t('tempo')}>
				{tempos && (
					<div className={styles.tempoChips}>
						{tempos.map((tempo) => (
							<Chip
								content={tempo.label}
								pressed={tempo.isRefined ?? 'false'}
								onClick={() => handleTempoChange(tempo.value)}
								key={tempo.value}
								data-testid={tempo.value}
							/>
						))}
					</div>
				)}
			</Fieldset>
			<div className={styles.divider} />
			<div className={styles.bpmWrapper}>
				<Fieldset legend={t('opening-bpm')}>
					<div className={styles.bpmPair}>
						<TextInput
							aria-label={t('opening-bpm-min')}
							defaultValue={defaultOpeningBpmMin}
							min={openingBpmMin}
							max={openingBpmMax}
							name={OPENING_BPM_MIN}
							onChange={debounce(handleBpmInput, 1000)}
							ref={openingBpmMinRef}
							type="number"
						/>
						<TextInput
							aria-label={t('opening-bpm-max')}
							defaultValue={defaultOpeningBpmMax}
							min={openingBpmMin}
							max={openingBpmMax}
							name={OPENING_BPM_MAX}
							onChange={debounce(handleBpmInput, 1000)}
							ref={openingBpmMaxRef}
							type="number"
						/>
					</div>
				</Fieldset>
				<Fieldset legend={t('closing-bpm')}>
					<div className={styles.bpmPair}>
						<TextInput
							aria-label={t('closing-bpm-min')}
							defaultValue={defaultClosingBpmMin}
							min={closingBpmMin}
							max={closingBpmMax}
							name={CLOSING_BPM_MIN}
							onChange={debounce(handleBpmInput, 1000)}
							ref={closingBpmMinRef}
							type="number"
						/>
						<TextInput
							aria-label={t('closing-bpm-max')}
							defaultValue={defaultClosingBpmMax}
							min={closingBpmMin}
							max={closingBpmMax}
							name={CLOSING_BPM_MAX}
							onChange={debounce(handleBpmInput, 1000)}
							ref={closingBpmMaxRef}
							type="number"
						/>
					</div>
				</Fieldset>
			</div>
			<div className={styles.divider} />
			<div className={styles.beatCalcWrapper}>
				<Button
					startIcon={<BeatCalculatorIcon />}
					onClick={showBeatCalculator}
					size="small"
					variant="secondary"
					data-testid="beat-calculator"
				>
					{t('beat-calculator')}
				</Button>
			</div>
		</div>
	)
}
export default TempoBpm
